import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { llmModelService } from '../../services/llmModelService';
import type { LLMModel, CreateLLMModelRequest, UpdateLLMModelRequest } from '../../types/llmModel';

interface LlmModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  model: LLMModel | null;
}

const PROVIDERS = ['openai', 'anthropic', 'google', 'mistral', 'other'];

export function LlmModelModal({ isOpen, onClose, onSuccess, model }: LlmModelModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateLLMModelRequest>({
    model_name: '',
    display_name: '',
    provider: 'openai',
    price_input: 0.15,
    price_output: 0.60,
    context_window: null,
    max_output_tokens: null,
    supports_json_mode: true,
    supports_vision: false,
    description: null,
    is_active: true,
  });

  useEffect(() => {
    if (model) {
      setFormData({
        model_name: model.model_name,
        display_name: model.display_name,
        provider: model.provider,
        price_input: typeof model.price_input === 'string' ? parseFloat(model.price_input) : model.price_input,
        price_output: typeof model.price_output === 'string' ? parseFloat(model.price_output) : model.price_output,
        context_window: model.context_window,
        max_output_tokens: model.max_output_tokens,
        supports_json_mode: model.supports_json_mode,
        supports_vision: model.supports_vision,
        description: model.description,
        is_active: model.is_active,
      });
    } else {
      setFormData({
        model_name: '',
        display_name: '',
        provider: 'openai',
        price_input: 0.15,
        price_output: 0.60,
        context_window: null,
        max_output_tokens: null,
        supports_json_mode: true,
        supports_vision: false,
        description: null,
        is_active: true,
      });
    }
  }, [model, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.model_name.trim()) {
      toast.error('Le nom du modèle est requis');
      return;
    }

    if (!formData.display_name.trim()) {
      toast.error('Le nom d\'affichage est requis');
      return;
    }

    try {
      setLoading(true);

      if (model) {
        const updateData: UpdateLLMModelRequest = { ...formData };
        await llmModelService.update(model.id, updateData);
        toast.success('Modèle mis à jour');
      } else {
        await llmModelService.create(formData);
        toast.success('Modèle créé');
      }

      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">
            {model ? 'Modifier le modèle' : 'Nouveau modèle LLM'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Model Name & Display Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du modèle (API) *
              </label>
              <input
                type="text"
                value={formData.model_name}
                onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                placeholder="gpt-4o-mini"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Identifiant API exact</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom d'affichage *
              </label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="GPT-4o Mini"
                required
              />
            </div>
          </div>

          {/* Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider *
            </label>
            <select
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {PROVIDERS.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix Input ($ / 1M tokens) *
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.price_input}
                onChange={(e) => setFormData({ ...formData, price_input: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix Output ($ / 1M tokens) *
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={formData.price_output}
                onChange={(e) => setFormData({ ...formData, price_output: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Context Window (tokens)
              </label>
              <input
                type="number"
                min="0"
                value={formData.context_window || ''}
                onChange={(e) => setFormData({ ...formData, context_window: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="128000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Output Tokens
              </label>
              <input
                type="number"
                min="0"
                value={formData.max_output_tokens || ''}
                onChange={(e) => setFormData({ ...formData, max_output_tokens: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="16384"
              />
            </div>
          </div>

          {/* Capabilities */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Capacités
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.supports_json_mode}
                  onChange={(e) => setFormData({ ...formData, supports_json_mode: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">JSON Mode</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.supports_vision}
                  onChange={(e) => setFormData({ ...formData, supports_vision: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Vision</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Actif</span>
              </label>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              rows={2}
              placeholder="Description optionnelle du modèle"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : model ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
